// QuestionScreen - Ask the cosmos with beautiful input
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Shuffle, Sparkles } from 'lucide-react';
import { Button } from '../common/Button';
import { Textarea } from '../common/Input';
import { PageContainer, GlassCard } from '../layout/PageContainer';
import { TarotCard } from '../tarot/TarotCard';
import { Modal } from '../common/Modal';
import { TAROT_DECK_WITH_INFO, MAJOR_ARCANA_NAMES, SUITS } from '../../utils/tarotData';

const MAX_QUESTION_LENGTH = 300;

export const QuestionScreen = ({
    question,
    setQuestion,
    onRead,
    isLoading,
    isManualSelectionMode,
    setIsManualSelectionMode,
    manuallySelectedCards,
    setManuallySelectedCards
}) => {
    const [isCardPickerOpen, setIsCardPickerOpen] = useState(false);
    const [currentSlotPicking, setCurrentSlotPicking] = useState(null);

    const openCardPicker = (slotIndex) => {
        setCurrentSlotPicking(slotIndex);
        setIsCardPickerOpen(true);
    };

    const handleCardSelectedFromPicker = (cardDetails, orientation, slotIndex) => {
        const newSelections = [...manuallySelectedCards];
        newSelections[slotIndex] = {
            name: cardDetails.name,
            emoji: cardDetails.emoji,
            orientation: orientation
        };
        setManuallySelectedCards(newSelections);
        setIsCardPickerOpen(false);
        setCurrentSlotPicking(null);
    };

    const allManualCardsSelected = manuallySelectedCards.every(card => card?.name && card?.orientation);
    const canSubmit = question.trim() && (!isManualSelectionMode || allManualCardsSelected);

    return (
        <PageContainer className="py-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-lg mx-auto"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        className="inline-block mb-4"
                        animate={{
                            rotate: [0, 5, -5, 0],
                            scale: [1, 1.05, 1]
                        }}
                        transition={{ duration: 4, repeat: Infinity }}
                    >
                        <Sparkles className="w-12 h-12 text-theme-accent mx-auto" />
                    </motion.div>
                    <h2 className="text-3xl sm:text-4xl font-serif text-theme mb-3">
                        Ask the Cosmos
                    </h2>
                    <p className="text-theme-secondary text-base sm:text-lg">
                        Focus your mind, take a deep breath, and type your question below.
                    </p>
                </div>

                {/* Question Input */}
                <GlassCard className="mb-6">
                    <Textarea
                        value={question}
                        onChange={(e) => {
                            if (e.target.value.length <= MAX_QUESTION_LENGTH) {
                                setQuestion(e.target.value);
                            }
                        }}
                        placeholder="What guidance do you seek?"
                        rows={4}
                        maxLength={MAX_QUESTION_LENGTH}
                        showCount
                        disabled={isLoading}
                        className="text-lg"
                    />
                </GlassCard>

                {/* Toggle Manual Selection */}
                <div className="flex justify-center mb-6">
                    <Button
                        variant={isManualSelectionMode ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => {
                            setIsManualSelectionMode(prev => !prev);
                            if (!isManualSelectionMode) {
                                setManuallySelectedCards(Array(5).fill(null));
                            }
                        }}
                        icon={isManualSelectionMode ? Shuffle : Edit3}
                        className="text-sm"
                    >
                        {isManualSelectionMode ? 'Switch to Random Draw' : 'Manually Select Cards?'}
                    </Button>
                </div>

                {/* Manual Card Selection */}
                <AnimatePresence>
                    {isManualSelectionMode && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <GlassCard className="mb-6 border-dashed border-theme-glass">
                                <h3 className="text-lg font-serif text-theme-secondary mb-4 text-center">
                                    Select Your 5 Cards
                                </h3>
                                <div className="space-y-3">
                                    {manuallySelectedCards.map((selectedCard, index) => (
                                        <CardSlot
                                            key={index}
                                            index={index}
                                            card={selectedCard}
                                            onClick={() => openCardPicker(index)}
                                        />
                                    ))}
                                </div>
                                <p className="text-xs text-theme-muted mt-4 text-center">
                                    {5 - manuallySelectedCards.filter(c => c).length} cards remaining
                                </p>
                            </GlassCard>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Submit Button */}
                <motion.div
                    whileHover={{ scale: canSubmit && !isLoading ? 1.02 : 1 }}
                    whileTap={{ scale: canSubmit && !isLoading ? 0.98 : 1 }}
                >
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => onRead(isManualSelectionMode)}
                        disabled={!canSubmit || isLoading}
                        loading={isLoading}
                        className="w-full text-xl py-4"
                    >
                        {isLoading ? 'Seeking Answers...' : 'Reveal My Path'}
                    </Button>
                </motion.div>
            </motion.div>

            {/* Card Picker Modal */}
            <CardPickerModal
                isOpen={isCardPickerOpen}
                onClose={() => {
                    setIsCardPickerOpen(false);
                    setCurrentSlotPicking(null);
                }}
                onCardSelected={handleCardSelectedFromPicker}
                currentSlotIndex={currentSlotPicking}
                previouslySelectedCards={manuallySelectedCards.filter((card, i) => i !== currentSlotPicking && card)}
            />
        </PageContainer>
    );
};

// Card Slot Component - uses theme colors
const CardSlot = ({ index, card, onClick }) => {
    if (card) {
        return (
            <motion.div
                className="p-3 rounded-xl flex items-center justify-between"
                style={{
                    background: 'var(--theme-glass-bg)',
                    border: '1px solid var(--theme-glass-border)',
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
            >
                <div className="flex items-center gap-3">
                    <span className="text-2xl">{card.emoji}</span>
                    <div>
                        <p style={{ color: 'var(--theme-text)' }} className="text-sm font-medium">{card.name}</p>
                        <p style={{ color: 'var(--theme-text-muted)' }} className="text-xs capitalize">{card.orientation}</p>
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={onClick}>
                    Change
                </Button>
            </motion.div>
        );
    }

    return (
        <motion.button
            onClick={onClick}
            className="w-full p-4 rounded-xl border-2 border-dashed transition-all text-sm"
            style={{
                borderColor: 'var(--theme-button-secondary-border)',
                color: 'var(--theme-text-muted)',
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{
                scale: 1.01,
                borderColor: 'var(--theme-accent)',
            }}
        >
            Click to Select Card {index + 1}
        </motion.button>
    );
};

// Card Picker Modal
const CardPickerModal = ({
    isOpen,
    onClose,
    onCardSelected,
    currentSlotIndex,
    previouslySelectedCards
}) => {
    const [activeTab, setActiveTab] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [cardPendingOrientation, setCardPendingOrientation] = useState(null);

    const TABS = ["All", "Major", ...SUITS];

    const isCardAlreadySelected = (cardName) => {
        return previouslySelectedCards.some(slot => slot?.name === cardName);
    };

    const filteredCards = React.useMemo(() => {
        let cards = TAROT_DECK_WITH_INFO;

        if (searchTerm) {
            cards = cards.filter(card =>
                card.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (activeTab === 'Major') {
            return cards.filter(card => MAJOR_ARCANA_NAMES.includes(card.name));
        } else if (SUITS.includes(activeTab)) {
            return cards.filter(card => card.name.includes(activeTab));
        }
        return cards;
    }, [searchTerm, activeTab]);

    const handleCardClick = (card) => {
        setCardPendingOrientation(card);
    };

    const handleOrientationChoice = (orientation) => {
        if (cardPendingOrientation) {
            onCardSelected(cardPendingOrientation, orientation, currentSlotIndex);
            setCardPendingOrientation(null);
            setSearchTerm('');
            setActiveTab('All');
        }
    };

    const handleClose = () => {
        setCardPendingOrientation(null);
        setSearchTerm('');
        setActiveTab('All');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={cardPendingOrientation
                ? `Set Orientation for ${cardPendingOrientation.name}`
                : `Select Card for Slot ${(currentSlotIndex ?? 0) + 1}`
            }
            size="2xl"
        >
            {!cardPendingOrientation ? (
                <>
                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search all cards..."
                        className="w-full p-3 mb-4 rounded-xl focus:outline-none"
                        style={{
                            background: 'var(--theme-glass-bg)',
                            border: '1px solid var(--theme-glass-border)',
                            color: 'var(--theme-text)',
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    {/* Tabs */}
                    <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
                        {TABS.map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors"
                                style={{
                                    background: activeTab === tab ? 'var(--theme-button-secondary-bg)' : 'transparent',
                                    border: activeTab === tab ? '1px solid var(--theme-accent)' : '1px solid transparent',
                                    color: activeTab === tab ? 'var(--theme-accent)' : 'var(--theme-text-muted)',
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Card Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-80 overflow-y-auto">
                        {filteredCards.length === 0 && (
                            <p className="col-span-full text-center py-8" style={{ color: 'var(--theme-text-muted)' }}>
                                {searchTerm ? `No cards match "${searchTerm}"` : 'No cards in this category'}
                            </p>
                        )}
                        {filteredCards.map(card => {
                            const isSelected = isCardAlreadySelected(card.name);
                            return (
                                <motion.button
                                    key={card.name}
                                    onClick={() => !isSelected && handleCardClick(card)}
                                    disabled={isSelected}
                                    className={`p-2 rounded-xl flex flex-col items-center justify-center h-20 transition-all ${isSelected ? 'opacity-40 cursor-not-allowed' : ''
                                        }`}
                                    style={{
                                        background: isSelected ? 'rgba(50,50,50,0.5)' : 'var(--theme-glass-bg)',
                                        border: isSelected ? '1px solid transparent' : '1px solid var(--theme-glass-border)',
                                    }}
                                    whileHover={!isSelected ? {
                                        scale: 1.05,
                                        boxShadow: '0 0 15px var(--theme-accent-glow)'
                                    } : undefined}
                                    whileTap={!isSelected ? { scale: 0.95 } : undefined}
                                >
                                    <span className="text-2xl">{card.emoji}</span>
                                    <span className="text-[9px] text-center mt-1 leading-tight line-clamp-2" style={{ color: 'var(--theme-text)' }}>
                                        {card.name}
                                    </span>
                                </motion.button>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className="flex flex-col items-center py-6">
                    <div className="mb-6">
                        <TarotCard
                            name={cardPendingOrientation.name}
                            emoji={cardPendingOrientation.emoji}
                            orientation="upright"
                            isClickable={false}
                        />
                    </div>
                    <p className="text-lg text-amber-200 mb-6">
                        Choose orientation for <strong className="text-amber-100">{cardPendingOrientation.name}</strong>
                    </p>
                    <div className="flex gap-4">
                        <Button
                            variant="secondary"
                            size="lg"
                            onClick={() => handleOrientationChoice('upright')}
                            className="border-emerald-500/30 hover:border-emerald-500/50"
                        >
                            Upright
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            onClick={() => handleOrientationChoice('reversed')}
                            className="border-rose-500/30 hover:border-rose-500/50"
                        >
                            Reversed
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default QuestionScreen;
